<template>
  <main>
    <div class="header__wrapper">
      <!-- Back button - Title - Last time saved -->
      <header class="header__details">
        <a-button type="link" class="back-button" @click="handleBack">
          <ArrowLeftOutlined />
          Volver
        </a-button>

        <div class="title">
          <h2 class="name">{{ sequenceDetails?.name || "Cargando..." }}</h2>

          <!--Estado de chatbot-->
          <!-- <a-tag v-if="sequenceDetails.status" :color="StatusProps[sequenceDetails.status].color">
            {{ StatusProps[sequenceDetails.status].text }}
          </a-tag> -->
        </div>

        <div class="save-time">
          Guardado por ultima vez:
          {{ dayjs(sequenceDetails?.updated_at).format("DD/MM/YYYY") || "..." }}
        </div>
      </header>
    </div>

    <!-- Tabs - Status switch -->
    <!-- v-model:activeKey="currentTab" -->
    <a-tabs :activeKey="currentTab" @update:activeKey="onTabChange" centered destroyInactiveTabPane>
      <template #leftExtra>
        <a-tooltip
          title="Esta plantilla se encuentra utilizando una variable que ha sido eliminada. Podrás volver a activar tu plantilla cuando esta variable sea reemplazada."
          placement="right"
          arrowPointAtCenter
        >
          <!-- <a-tag v-if="sequenceDetails?.deleted_variable" color="warning">
            <WarningFilled />
            Variable eliminada
          </a-tag> -->
        </a-tooltip>
      </template>

      <template #rightExtra>
        <div class="extra-action">
          <DisableSwitch
            inTemplate
            v-if="sequenceDetails"
            :sequence="sequenceDetails"
            @update="fetchSequence"
          />
        </div>
      </template>

      <a-tab-pane :key="TemplateTabsKey.Build" :tab="TabSectionName[TemplateTabsKey.Build]">
        <EditSequenceSection
          :sequence="sequenceDetails"
          :nodes="sequenceNodes"
          @update:nodes="fetchSequence(); fetchNodes();"
        />
      </a-tab-pane>

      <a-tab-pane :key="TemplateTabsKey.General" :tab="TabSectionName[TemplateTabsKey.General]">
        <div class="container-tab">
          <h3>Configuración general</h3>

          <ConfigurationStep
            ref="currentFormRef"
            :key="formKey"
            :sequence="sequenceDetails"
            fromDetailTemplate
            :disabled="sequenceDetails?.status === SequenceStatus.Active"
            @toggleButton="isUpdateButtonDisabled = $event"
          />
        </div>
      </a-tab-pane>

      <a-tab-pane :key="TemplateTabsKey.Schedule" :tab="TabSectionName[TemplateTabsKey.Schedule]">
        <div class="container-tab">
          <h3>Horario de ejecución</h3>

          <ScheduleStep
            ref="currentFormRef"
            :key="formKey"
            :sequence="sequenceDetails"
            fromDetailTemplate
            :disabled="sequenceDetails?.status === SequenceStatus.Active"
            @toggleButton="isUpdateButtonDisabled = $event"
          />
        </div>
      </a-tab-pane>

      <a-tab-pane
        :key="TemplateTabsKey.Identifiers"
        :tab="TabSectionName[TemplateTabsKey.Identifiers]"
        v-if="sequenceDetails?.sequence_type == SequenceType.Multichannel"
      >
        <div class="container-tab">
          <h3>Identificadores vinculados</h3>

          <IdentifiersStep
            ref="currentFormRef"
            :key="formKey"
            :sequence="sequenceDetails"
            :portfolio-id="sequenceDetails?.portfolio.id"
            :disabled="sequenceDetails?.status === SequenceStatus.Active"
            @activate-button="isUpdateButtonDisabled = $event"
          />
        </div>
      </a-tab-pane>

      <a-tab-pane
        :key="TemplateTabsKey.Users"
        :tab="TabSectionName[TemplateTabsKey.Users]"
        v-if="sequenceDetails?.sequence_type == SequenceType.AutomaticProgressive"
      >
        <div class="container-tab">
          <h3>Usuarios asignados</h3>

          <UsersStep
            ref="currentFormRef"
            :key="formKey"
            :sequence="sequenceDetails"
            :portfolio-id="sequenceDetails?.portfolio.id"
            fromDetailTemplate
            :disabled="sequenceDetails?.status === SequenceStatus.Active"
            @toggleButton="isUpdateButtonDisabled = $event"
          />
        </div>
      </a-tab-pane>

      <a-tab-pane :key="TemplateTabsKey.Leads" :tab="TabSectionName[TemplateTabsKey.Leads]">
        <div class="container-tab">
          <h3>Integración de Leads</h3>

          <LeadsStep
            @toggleButton="isUpdateButtonDisabled = $event"
          />
        </div>
      </a-tab-pane>
    </a-tabs>

    <div class="footer-wrapper">
      <footer v-if="currentTab !== TemplateTabsKey.Build && currentTab !== TemplateTabsKey.Leads">
        <a-button @click="handleReset" :disabled="isActive"> Cancelar </a-button>
        <a-button
          type="primary"
          @click="handleUpdate"
          :disabled="isUpdateButtonDisabled || isActive"
          :loading="saving"
        >
          Actualizar {{ TabSectionName[currentTab] }}
        </a-button>
      </footer>
    </div>
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted, provide, computed, watch, createVNode } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ArrowLeftOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import dayjs from "dayjs";

import { TemplateTabsKey } from "@/@types/flows/sequences/components";
import { Sequence, SequenceNode, SequenceStatus } from "@/@types/flows/sequences";
import { sequenceService } from "@/app/flows/sequences/services/Sequence.service";
import { sequenceNodeService } from "@/app/flows/sequences/services/SequenceNodes.service";

import DisableSwitch from "@/app/flows/sequences/components/buttons/DisableSwitch.vue";
import ConfigurationStep from "@/app/flows/sequences/components/steps/ConfigurationStep.vue";
import ScheduleStep from "@/app/flows/sequences/components/steps/ScheduleStep.vue";
import IdentifiersStep from "@/app/flows/sequences/components/steps/IdentifiersStep.vue";
import UsersStep from "@/app/flows/sequences/components/steps/UsersStep.vue";
import LeadsStep from "@/app/flows/sequences/components/steps/LeadsStep.vue";
import EditSequenceSection from "@/app/flows/sequences/components/sections/EditSequenceSection.vue";
import { SequenceType } from "@/app/flows/sequences/utils/enums";
import { Modal } from "ant-design-vue";

const TabSectionName = {
  [TemplateTabsKey.Build]: "Construir secuencia",
  [TemplateTabsKey.General]: "Datos generales",
  [TemplateTabsKey.Schedule]: "Horario",
  [TemplateTabsKey.Identifiers]: "Identificadores vinculados",
  [TemplateTabsKey.Users]: "Usuarios asignados",
  [TemplateTabsKey.Leads]: "Integración de Leads",
};

const router = useRouter();
const route = useRoute();

const sequenceDetails = ref<Sequence>();
provide("sequenceDetails", sequenceDetails);

const sequenceNodes = ref<SequenceNode[]>([]);

const currentOpenNode = ref<number>();
provide("currentOpenNode", currentOpenNode);

const handleBack = () => router.push("/flows/sequences");

const isActive = computed(() => sequenceDetails.value?.status === SequenceStatus.Active);

const fetchSequence = async () => {
  const sequenceId = Number(route.params.id);

  sequenceDetails.value = await sequenceService.getById(sequenceId);
};

const fetchNodes = async () => {
  const sequenceId = Number(route.params.id);
  sequenceNodes.value = await sequenceNodeService.getNodes(sequenceId);
};

// provide("fetchSequence", fetchSequence);
// provide("fetchNodes", fetchNodes);

onMounted(() => {
  Promise.all([fetchSequence(), fetchNodes()]);
});

const currentTab = ref(TemplateTabsKey.Build);
provide('currentTab', currentTab);

const isUpdateButtonDisabled = ref(true);
const saving = ref(false);
const formKey = ref(1);
const currentFormRef =
  ref<InstanceType<typeof ConfigurationStep | typeof ScheduleStep | typeof UsersStep>>();

const handleUpdate = async () => {
  const sequenceId = Number(route.params.id);

  const formState = currentFormRef.value?.getFormState();

  if (currentTab.value == TemplateTabsKey.General) await currentFormRef.value?.formRef?.validate();

  try {
    if (!formState) throw new Error("No data to save");

    await sequenceService.update(sequenceId, formState);

    await fetchSequence();

    message.success("Se guardaron los cambios");

    isUpdateButtonDisabled.value = true;
  } catch (error) {
    console.error(error);
  }
};

const handleReset = () => {
  formKey.value++;
};

const onTabChange = (nextKey: number) => {
  if (!isUpdateButtonDisabled.value) {
    Modal.confirm({
      icon: createVNode(QuestionCircleOutlined),
      title: "¿Deseas descartar los cambios realizados?",
      content: "Recuerda que si no guardas los cambios registrados, estos se perderán.",
      okText: "Sí, descartar cambios",
      cancelText: "No, seguir editando",
      centered: true,
      onOk: async () => {
        currentTab.value = nextKey;
      },
    });
  } else {
    currentTab.value = nextKey;
  }
  if (currentTab.value == nextKey && nextKey == TemplateTabsKey.Build)
    fetchNodes();
  if (currentTab.value == nextKey && nextKey == TemplateTabsKey.Identifiers)
    fetchSequence();
};

watch(currentTab, () => {
  isUpdateButtonDisabled.value = true;
});
</script>

<style scoped lang="sass">
.button
  padding: 0

.divider
  background-color: #00000026
  height: 1.2em

h3
  font-size: 16px
  font-weight: 600
  color: $gray-9
  margin-bottom: 16px
  padding-bottom: 8px
  border-bottom: 1px solid $gray-5

main
  background-color: $geekblue-1
  height: 100%
  display: flex
  flex-direction: column

.header
  &__wrapper
    width: 100%
    padding: 6px 12px 0
    background-color: $white

  &__details
    display: flex
    justify-content: center
    align-items: center
    position: relative
    width: 100%

    .back-button
      padding: 0
      position: absolute
      left: 0

    .title
      display: flex
      align-items: center
      gap: 8px

      .name
        font-size: 16px
        font-weight: 600
        color: $gray-9
        margin: 0

    .save-time
      font-style: italic
      font-weight: 300
      font-size: 12px
      position: absolute
      right: 0

.extra-action
  display: flex
  align-items: center

:deep(.ant-tabs-nav)
  background-color: white
  // margin-bottom: 12px !important
  margin-bottom: 0 !important

:deep(.ant-tabs)
  // height: calc(100% - 32px)
  flex: 1 1 auto
  min-height: 0

// :deep(.ant-tabs-content-holder)


:deep(.ant-tabs-content)
  height: 100%

:deep(.ant-tabs-nav .ant-tabs-extra-content:nth-child(1))
  position: absolute
  left: 12px

:deep(.ant-tabs-nav .ant-tabs-extra-content:nth-child(3))
  position: absolute
  right: 12px

:deep(.ant-tabs-tabpane)
  height: 100%

:deep(.ant-tabs-tabpane:not(:first-child))
  background-color: white

.ant-spin-nested-loading
  height: calc(100% - 30px)

:deep(.ant-spin-container)
  height: 100%

.container-tab
  margin: 1rem auto 0
  max-width: 50%
  height: calc(100% - 1rem)

.footer-wrapper
  background-color: white

footer
  margin: 0 auto
  max-width: 50%
  display: flex
  justify-content: flex-end
  gap: 12px
  padding: 12px 24px
  width: 100%
  border-top: 1px solid $gray-4
</style>
